import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Check, Trash2, UploadCloud } from "lucide-react";

import { Button, Card, CardContent } from "@runcheetah/ui";

export type CreativeLogoOption = {
  id: string;
  objectKey: string;
  signedUrl: string;
  label: string | null;
  isPrimary?: boolean;
};

const LOGO_UPLOAD_ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_LOGO_UPLOAD_BYTES = 5 * 1024 * 1024;
const SUPPORTED_LOGO_UPLOAD_TYPES = new Set(LOGO_UPLOAD_ACCEPT.split(","));

export function CreativeLogoPicker({
  logos,
  selectedObjectKey,
  disabled = false,
  uploading = false,
  onSelectLogo,
  onDeleteLogo,
  onUploadLogo,
}: {
  logos: CreativeLogoOption[];
  selectedObjectKey: string | null;
  disabled?: boolean;
  uploading?: boolean;
  onSelectLogo: (logo: CreativeLogoOption) => void;
  onDeleteLogo?: (logo: CreativeLogoOption) => Promise<void> | void;
  onUploadLogo: (file: File) => Promise<void> | void;
}) {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAllLogos, setShowAllLogos] = useState(false);
  const [canScrollFurther, setCanScrollFurther] = useState(false);
  const [pendingDeleteLogo, setPendingDeleteLogo] = useState<CreativeLogoOption | null>(null);
  const hasMoreLogos = logos.length > 4;
  const visibleLogos = showAllLogos || !hasMoreLogos ? logos : logos.slice(0, 4);

  const updateScrollHint = useCallback(() => {
    const element = scrollContainerRef.current;
    if (!element || !showAllLogos) {
      setCanScrollFurther(false);
      return;
    }

    setCanScrollFurther(element.scrollTop + element.clientHeight < element.scrollHeight - 2);
  }, [showAllLogos]);

  useEffect(() => {
    updateScrollHint();
  }, [logos.length, updateScrollHint]);

  const handleFileSelected = async (file: File | undefined) => {
    if (!file) return;

    if (!SUPPORTED_LOGO_UPLOAD_TYPES.has(file.type)) {
      setError(
        intl.formatMessage({
          id: "creativeLogoPicker.unsupportedFile",
          defaultMessage: "Upload a PNG, JPG, WebP, or SVG logo.",
        }),
      );
      return;
    }

    if (file.size > MAX_LOGO_UPLOAD_BYTES) {
      setError(
        intl.formatMessage({
          id: "creativeLogoPicker.fileTooLarge",
          defaultMessage: "Logo files must be 5 MB or smaller.",
        }),
      );
      return;
    }

    setError(null);
    try {
      await onUploadLogo(file);
    } catch {
      setError(
        intl.formatMessage({
          id: "creativeLogoPicker.uploadFailed",
          defaultMessage: "Could not upload the logo. Try again.",
        }),
      );
    } finally {
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const confirmDeleteLogo = async () => {
    if (!onDeleteLogo || !pendingDeleteLogo) return;

    setError(null);
    try {
      await onDeleteLogo(pendingDeleteLogo);
      setPendingDeleteLogo(null);
    } catch {
      setError(
        intl.formatMessage({
          id: "creativeLogoPicker.deleteFailed",
          defaultMessage: "Could not delete the logo. Try again.",
        }),
      );
    }
  };

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground/48">
          <FormattedMessage id="creativeLogoPicker.label" defaultMessage="Logo asset" />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={LOGO_UPLOAD_ACCEPT}
          className="hidden"
          onChange={(event) => {
            void handleFileSelected(event.currentTarget.files?.[0]);
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 rounded-full px-2 text-[11px] text-muted-foreground hover:text-foreground"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
        >
          <UploadCloud className="h-3.5 w-3.5" aria-hidden="true" />
          {uploading ? (
            <FormattedMessage id="creativeLogoPicker.uploading" defaultMessage="Uploading" />
          ) : (
            <FormattedMessage id="creativeLogoPicker.upload" defaultMessage="Upload" />
          )}
        </Button>
      </div>

      {logos.length > 0 ? (
        <>
          <div className="relative">
            <div
              ref={scrollContainerRef}
              className={showAllLogos ? "max-h-72 overflow-y-auto pr-1" : ""}
              onScroll={updateScrollHint}
            >
              <div className="grid grid-cols-2 gap-2">
                {visibleLogos.map((logo) => {
                  const active = logo.objectKey === selectedObjectKey;
                  return (
                    <div
                      key={logo.id}
                      className={`group min-w-0 rounded-2xl border p-2 text-left transition-colors disabled:pointer-events-none disabled:opacity-55 ${
                        active
                          ? "border-primary/35 bg-primary/[0.06]"
                          : "border-border/60 bg-background/70 hover:border-foreground/20"
                      }`}
                    >
                      <button
                        type="button"
                        disabled={disabled || uploading}
                        onClick={() => onSelectLogo(logo)}
                        className="block w-full min-w-0 text-left disabled:pointer-events-none disabled:opacity-55"
                      >
                        <div className="relative flex aspect-[4/3] items-center justify-center rounded-xl bg-muted/60 p-2">
                          <img
                            src={logo.signedUrl}
                            alt={
                              logo.label ??
                              intl.formatMessage({
                                id: "creativeLogoPicker.logoAlt",
                                defaultMessage: "Uploaded logo",
                              })
                            }
                            className="max-h-full max-w-full object-contain"
                            loading="lazy"
                          />
                          {active ? (
                            <span className="absolute right-1.5 top-1.5 rounded-full bg-primary p-1 text-primary-foreground shadow-sm">
                              <Check className="h-3 w-3" aria-hidden="true" />
                            </span>
                          ) : null}
                        </div>
                      </button>
                      <div className="mt-1.5 flex min-w-0 items-center gap-1.5">
                        <p className="min-w-0 flex-1 truncate text-[11px] font-medium text-foreground/68">
                          {logo.label ||
                            (logo.isPrimary
                              ? intl.formatMessage({
                                  id: "creativeLogoPicker.primaryLogo",
                                  defaultMessage: "Primary logo",
                                })
                              : intl.formatMessage({
                                  id: "creativeLogoPicker.untitledLogo",
                                  defaultMessage: "Logo",
                                }))}
                        </p>
                        {onDeleteLogo && !logo.isPrimary && !logo.id.startsWith("preview:") ? (
                          <button
                            type="button"
                            disabled={disabled || uploading}
                            onClick={() => setPendingDeleteLogo(logo)}
                            className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-55"
                            aria-label={intl.formatMessage({
                              id: "creativeLogoPicker.deleteLogo",
                              defaultMessage: "Delete logo",
                            })}
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {canScrollFurther ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 rounded-b-2xl bg-gradient-to-t from-background via-background/85 to-transparent" />
            ) : null}
          </div>
          {hasMoreLogos ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 w-full rounded-full text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setShowAllLogos((current) => !current)}
            >
              {showAllLogos ? (
                <FormattedMessage id="creativeLogoPicker.showLess" defaultMessage="Show less" />
              ) : (
                <FormattedMessage
                  id="creativeLogoPicker.showMore"
                  defaultMessage="Show {count} more"
                  values={{ count: logos.length - 4 }}
                />
              )}
            </Button>
          ) : null}
        </>
      ) : (
        <div className="rounded-2xl border border-dashed border-border/70 bg-muted/30 px-3 py-4 text-center text-xs leading-5 text-muted-foreground">
          <FormattedMessage
            id="creativeLogoPicker.empty"
            defaultMessage="Upload a logo to use it in this creative."
          />
        </div>
      )}

      {error ? <p className="text-xs leading-5 text-destructive">{error}</p> : null}

      {pendingDeleteLogo ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-background/75 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md rounded-3xl border-border/70 shadow-xl">
            <CardContent className="space-y-5 p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                  <Trash2 className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold tracking-tight text-foreground">
                    <FormattedMessage
                      id="creativeLogoPicker.deleteConfirmTitle"
                      defaultMessage="Delete this logo?"
                    />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="creativeLogoPicker.deleteConfirmDescription"
                      defaultMessage="This removes the logo from every asset that uses it. Those assets will fall back to your primary logo."
                    />
                  </p>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPendingDeleteLogo(null)}
                  disabled={uploading}
                >
                  <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void confirmDeleteLogo()}
                  disabled={uploading}
                >
                  <FormattedMessage
                    id="creativeLogoPicker.deleteConfirmAction"
                    defaultMessage="Delete logo"
                  />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
